import { BehaviorSubject, Subject, interval, takeUntil, filter, switchMap, tap, catchError, of, startWith, Subscription, merge } from 'rxjs';
import { ArrayResponse, BookingRequest, BookingRequestStatus } from 'common';
import { BookingRequestsService } from './booking.requests.service';
import { VendorService } from '../../../services/vendorService';
import { Injectable, inject, OnDestroy } from '@angular/core';
import { AuthService } from '@ng-shared';

/**
 * Service for tracking and polling Assigned booking request counts.
 * Used to display badge notifications in the navigation.
 */
@Injectable({
  providedIn: 'root'
})
export class BookingRequestsTagService implements OnDestroy {

  private readonly _requestsCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public requestsCount$ = this._requestsCountSubject.asObservable();

  private readonly _badgeTextSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public badgeText$ = this._badgeTextSubject.asObservable();

  private readonly _hasNewRequestsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public hasNewRequests$ = this._hasNewRequestsSubject.asObservable();

  private readonly _immediateFetchSubject: Subject<void> = new Subject<void>();

  private destroy$ = new Subject<void>();
  private pollingPaused = false;

  private polSub: Subscription | undefined;

  private readonly POLL_INTERVAL_MS: number = 120000; // 2 minutes

  private readonly _authService: AuthService = inject(AuthService);

  private readonly _vendorService: VendorService = inject(VendorService);

  private readonly _bookingRequestsService: BookingRequestsService = inject(BookingRequestsService);

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async startPolling(): Promise<void> {
    this.pollingPaused = false;

    if (this.polSub) {
      return;
    }

    // Load vendor before initial poll to successfully fetch requests.
    await this._vendorService.loadVendorIfNotSet();

    this.polSub = merge(
      interval(this.POLL_INTERVAL_MS),
      this._immediateFetchSubject.asObservable()
    )
      .pipe(
        startWith(0),  // Trigger immediately on subscription

        filter(() => this._authService.isAuthenticated),

        switchMap(() => {
          // Skip if polling is paused or no vendor loaded
          if (this.pollingPaused || !this._vendorService.currentVendor?.id) {
            return of(null);
          }

          return this.fetchAssignedCount();
        }),

        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public pausePolling(): void {
    this.pollingPaused = true;
  }

  private fetchAssignedCount() {
    const vendorId: number | undefined = this._vendorService.currentVendor?.id;

    if (!vendorId) {
      return of(null);
    }

    return this._bookingRequestsService.fetchBookingRequests(
      { vendorId, statuses: [BookingRequestStatus.Assigned], filter: '' },
      1, // page 1
      1 // size 1
    )
      .pipe(
        tap((response: ArrayResponse<BookingRequest>) => {
          const total: number = response.total || 0;

          this._requestsCountSubject.next(total);

          this._hasNewRequestsSubject.next(total > 0);

          // Show "5+" if we got 5 results (indicating more may exist)
          if (total >= 5) {
            this._badgeTextSubject.next('5+');
            this.pollingPaused = true;  // Pause polling when at capacity
          }
          else if (total > 0) {
            this._badgeTextSubject.next(total.toString());
          }
          else {
            this._badgeTextSubject.next('');
          }
        }),
        catchError(error => {
          console.error('Error fetching booking request count:', error);
          return of(null);
        })
      );
  }

  /** 
   * Called after a booking request update to refresh the count and resume polling.
   */
  public refreshAndResume(): void {
    this.pollingPaused = false;
    this._immediateFetchSubject.next();
  }

  /** 
   * Mark requests as viewed (clears the notification indicator)
   */
  public markRequestsAsViewed(): void {
    this._hasNewRequestsSubject.next(false);
  }
}
